import { collectLeaves } from '@/pages/game/live-events/model/contract.js';

const NUMBER_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, twelve: 12, fourteen: 14 };
const DOWN = /\b(cut|reduce|lower|decrease|cheaper|drop|discount|less|off|down)\b/;
const UP = /\b(raise|increase|boost|bump|more|extra|up)\b/;
const PRICE_WORDS = /\b(cheaper|discount|off|sale|pricier|expensive)\b/;
const QUESTION = /^(how|what|why|which|when|did|does|do|is|are|find out|check|compare)\b/;
const CHANGE_WORDS = new Set(['cheaper', 'more', 'less', 'higher', 'lower', 'longer', 'shorter', 'instead']);
const ADJUST = 'Adjust';
const BLOCKS = new Set(['NEEDS_INPUT', 'NOT_ACTIONABLE', 'FAILED']);

const tokens = (text) => String(text || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((w) => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w));

const display = (v) => (typeof v === 'string' ? `"${v}"` : String(v));

const scaleOf = (n) => (String(n).split('.')[1] || '').length;

const nextValue = (current, op, value) => {
    if (op === 'SET') return value;
    const out = op === 'MULTIPLY' ? current * value : current + value;
    if (Number.isInteger(current)) return Math.round(out);
    return Number(out.toFixed(Math.max(2, scaleOf(current))));
};

const parseChange = (raw, fallbackDown = false) => {
    const text = String(raw || '').toLowerCase();
    const times = /(?:^|\s)[x×]\s?(\d+(?:\.\d+)?)\b/.exec(text) || /\b(\d+(?:\.\d+)?)\s?[x×](?=\s|$|[,.])/.exec(text);
    if (times) return { op: 'MULTIPLY', value: Number(times[1]) };
    if (/\bdouble\b/.test(text)) return { op: 'MULTIPLY', value: 2 };
    if (/\btriple\b/.test(text)) return { op: 'MULTIPLY', value: 3 };
    if (/\bhalve\b|\bhalf\b/.test(text)) return { op: 'MULTIPLY', value: 0.5 };
    const pct = /([+-])?\s?(\d+(?:\.\d+)?)\s?%/.exec(text);
    if (pct) {
        const down = pct[1] === '-' || (pct[1] !== '+' && (DOWN.test(text) || (!UP.test(text) && fallbackDown)));
        const p = Number(pct[2]) / 100;
        return { op: 'MULTIPLY', value: Number((down ? 1 - p : 1 + p).toFixed(4)) };
    }
    if (/\b(enable|turn on|switch on)\b|\bto (true|on)\b|^\s*(on|true|yes)\s*$/.test(text)) return { op: 'SET', value: true };
    if (/\b(disable|turn off|switch off)\b|\bto (false|off)\b|^\s*(off|false|no)\s*$/.test(text)) return { op: 'SET', value: false };
    const set = /\b(?:to|=|at|make it|set it)\s*\$?(-?\d+(?:\.\d+)?)\b/.exec(text) || /^\s*\$?(-?\d+(?:\.\d+)?)\s*$/.exec(text);
    if (set) return { op: 'SET', value: Number(set[1]) };
    const add = /(?:\+|\bplus\b|\badd\b|\bup by\b)\s*(\d+(?:\.\d+)?)/.exec(text);
    if (add) return { op: 'ADD', value: Number(add[1]) };
    const sub = /(?:\bminus\b|\bdown by\b)\s*(\d+(?:\.\d+)?)/.exec(text);
    if (sub) return { op: 'ADD', value: -Number(sub[1]) };
    return null;
};

const parseHours = (raw) => {
    const m = /\bfor\s+(?:the\s+(?:first|next)\s+)?(\d+|one|two|three|four|five|six|seven|eight|nine|ten|twelve|fourteen)\s*(hours?|h|days?|d|weeks?)\b/.exec(String(raw || '').toLowerCase());
    if (!m) return null;
    const n = Number(m[1]) || NUMBER_WORDS[m[1]];
    if (/^w/.test(m[2])) return n * 168;
    return /^d/.test(m[2]) ? n * 24 : n;
};

const leavesOf = (inventory) => inventory.configs.flatMap((file) => collectLeaves(file.content)
    .filter((leaf) => leaf.value !== null && typeof leaf.value !== 'object')
    .map((leaf) => ({ fileId: file.fileId, fileName: file.name, key: leaf.path, value: leaf.value, words: [...new Set(tokens(leaf.path))] })));

const fits = (leaf, change) => {
    if (!change) return true;
    if (change.op !== 'SET') return typeof leaf.value === 'number';
    return typeof leaf.value === typeof change.value;
};

const matchKeys = (text, leaves, change) => {
    const lower = text.toLowerCase();
    const exact = leaves.filter((l) => lower.includes(l.key.toLowerCase()));
    if (exact.length) return { keys: exact, candidates: [] };
    const have = new Set(tokens(text));
    if (PRICE_WORDS.test(lower)) have.add('price');
    const scored = leaves.filter((l) => fits(l, change))
        .map((l) => ({ l, score: l.words.filter((w) => have.has(w)).length }))
        .filter((x) => x.score > 0);
    if (!scored.length) return { keys: [], candidates: [] };
    const best = Math.max(...scored.map((x) => x.score));
    const top = scored.filter((x) => x.score === best).map((x) => x.l);
    return best >= 2 && top.length === 1 ? { keys: top, candidates: [] } : { keys: [], candidates: top };
};

const ruleBreak = (rule, key, value) => {
    const n = Number(value);
    if (rule.op === 'MIN' && Number.isFinite(n) && n < rule.value) return `${key} ${display(value)} is below the minimum ${rule.value}`;
    if (rule.op === 'MAX' && Number.isFinite(n) && n > rule.value) return `${key} ${display(value)} is above the maximum ${rule.value}`;
    if (rule.op === 'ONE_OF' && rule.values?.length && !rule.values.includes(String(value))) return `${key} ${display(value)} is not one of ${rule.values.join(', ')}`;
    if (rule.op === 'NEVER_EMPTY' && (value == null || String(value).trim() === '')) return `${key} is empty`;
    return null;
};

const typeProblem = (leaf, change) => {
    if (change.op !== 'SET' && typeof leaf.value !== 'number') {
        return `${leaf.key} is not a number (${display(leaf.value)}), so it can't be ${change.op === 'MULTIPLY' ? 'multiplied' : 'shifted'}.`;
    }
    if (change.op === 'SET' && typeof leaf.value !== typeof change.value) {
        return `${leaf.key} is a ${typeof leaf.value} today (${display(leaf.value)}); ${display(change.value)} is a ${typeof change.value}.`;
    }
    if (change.op === 'SET' && Number.isInteger(leaf.value) && !Number.isInteger(change.value)) {
        return `${leaf.key} takes whole numbers; ${change.value} isn't one.`;
    }
    return null;
};

const outcome = (status, extra = {}) => ({
    status, actions: [], changes: [], questions: [], note: null, sources: [], citedKnowledgeIds: [], ruleBreaks: [], warnings: [], outputs: [], dependsOn: [], ...extra,
});

const setValue = (leaf, change) => ({ type: 'SET_VALUE', fileId: leaf.fileId, key: leaf.key, op: change.op, value: change.value, segmentId: null, hours: null });
const audienceAction = (segmentId) => ({ type: 'AUDIENCE', fileId: null, key: null, op: null, value: null, segmentId, hours: null });
const windowAction = (hours) => ({ type: 'WINDOW', fileId: null, key: null, op: null, value: null, segmentId: null, hours });

const nounOf = (title) => {
    const last = String(title).toLowerCase().replace(/[^a-z\s]/g, ' ').trim().split(/\s+/).pop();
    return last && !CHANGE_WORDS.has(last) && last.length > 3 ? last : null;
};

const mentionedSegments = (text, segments) => {
    const have = new Set(tokens(text));
    const lower = text.toLowerCase();
    return segments.filter((s) => {
        const name = s.name.toLowerCase();
        if (lower.includes(name)) return true;
        const first = tokens(s.name)[0];
        return !!first && first.length >= 5 && have.has(first);
    });
};

const citedFor = (text, knowledge) => {
    const have = new Set(tokens(text));
    return knowledge.filter((k) => tokens(k.title).some((w) => w.length >= 5 && have.has(w))).map((k) => k.id);
};

const answerFor = (inventory, cited) => {
    const run = inventory.lastRuns[0];
    if (run) {
        const said = [run.done, run.result].filter(Boolean).map((s) => s.trim().replace(/\.?$/, '.')).join(' ');
        return outcome('COMPLETED', { note: `${run.title}: ${said}`, sources: [`Run: ${run.title}`], citedKnowledgeIds: cited });
    }
    const card = inventory.knowledge.find((k) => cited.includes(k.id));
    if (card) return outcome('COMPLETED', { note: `${card.title}. ${card.body}`, citedKnowledgeIds: [card.id] });
    return outcome('COMPLETED', { note: "This event hasn't recorded anything that answers that yet.", citedKnowledgeIds: cited });
};

const processOne = (task, inventory, working) => {
    if (task.actor === 'MANUAL') return outcome('MANUAL');

    const answers = task.answers || [];
    const adjusts = answers.filter((a) => a.question === ADJUST).map((a) => a.answer);
    const replies = answers.filter((a) => a.question !== ADJUST).map((a) => a.answer);
    const base = [task.title, task.description].filter(Boolean).join(' ');
    const text = [base, ...replies, ...adjusts].join(' ');
    const cited = citedFor(text, inventory.knowledge);

    if (QUESTION.test(task.title.trim().toLowerCase()) || /\?\s*$/.test(task.title)) return answerFor(inventory, cited);

    const titleChange = parseChange(`${base} ${replies.join(' ')}`);
    const adjusted = [...adjusts].reverse().map((a) => parseChange(a, titleChange?.op === 'MULTIPLY' && titleChange.value < 1)).find(Boolean);
    const change = adjusted || titleChange;
    const leaves = leavesOf(inventory);
    const hours = parseHours(text);
    const audience = mentionedSegments(text, inventory.segments);
    const { keys, candidates } = matchKeys(text, leaves, change);
    const wantsAll = replies.some((r) => /\b(both|all|every)\b/i.test(r));
    const targets = keys.length ? keys : (wantsAll ? candidates : []);

    const ask = (question) => outcome('NEEDS_INPUT', { questions: [question], citedKnowledgeIds: cited });

    if (!change) {
        if (targets.length) {
            const leaf = targets[0];
            return ask({
                text: `How should ${leaf.key} change?`,
                options: typeof leaf.value === 'boolean' ? ['on', 'off'] : typeof leaf.value === 'number' ? ['x2', '-20%', '+20%'] : [],
            });
        }
        if (hours == null && !audience.length) {
            if (!inventory.configs.length) return outcome('NOT_ACTIONABLE', { note: 'No config is linked to this event, so there is nothing it can change.' });
            return outcome('NOT_ACTIONABLE', { note: `There is nothing in this event's configs to ${task.title.charAt(0).toLowerCase()}${task.title.slice(1).replace(/\.$/, '')}.` });
        }
    } else if (!targets.length) {
        if (candidates.length) {
            const noun = nounOf(task.title);
            const options = candidates.slice(0, 3).map((l) => l.key);
            if (candidates.length === 2) options.push('both');
            if (candidates.length > 2) options.push('all of them');
            return ask({ text: noun ? `Which ${noun}?` : 'Which value should change?', options });
        }
        return ask(inventory.configs.length
            ? { text: 'Which value should change?', options: leaves.filter((l) => fits(l, change)).slice(0, 3).map((l) => l.key) }
            : { text: 'No config is linked to this event. Which file should change?', options: [] });
    }

    const questions = [];
    const actions = [];
    const changes = [];
    const ruleBreaks = [];
    const dependsOn = new Set();
    if (change) {
        targets.forEach((leaf) => {
            const problem = typeProblem(leaf, change);
            if (problem) {
                questions.push({ text: problem, options: [] });
                return;
            }
            const slot = `${leaf.fileId}:${leaf.key}`;
            const earlier = working.get(slot);
            if (earlier) dependsOn.add(earlier.by);
            const before = earlier ? earlier.value : leaf.value;
            const after = nextValue(before, change.op, change.value);
            const broken = inventory.rules.filter((r) => r.fileId === leaf.fileId && r.key === leaf.key)
                .map((r) => ruleBreak(r, leaf.key, after)).filter(Boolean);
            if (broken.length) {
                ruleBreaks.push(...broken);
                return;
            }
            working.set(slot, { value: after, by: task.id });
            actions.push(setValue(leaf, change));
            changes.push({ fileId: leaf.fileId, fileName: leaf.fileName, key: leaf.key, before, after });
        });
    }
    audience.forEach((s) => actions.push(audienceAction(s.id)));
    if (hours != null) actions.push(windowAction(hours));

    if (questions.length) return outcome('NEEDS_INPUT', { questions: questions.slice(0, 3), citedKnowledgeIds: cited });
    return outcome('COMPLETED', {
        actions, changes, ruleBreaks, citedKnowledgeIds: cited, dependsOn: [...dependsOn],
    });
};

const keep = (task, working) => {
    task.result.changes.forEach((c) => working.set(`${c.fileId}:${c.key}`, { value: c.after, by: task.id }));
    return task.result;
};

// `run` names the AI tasks that run again (null: all of them); every other AI task keeps its result.
export const processTasks = (tasks, inventory, processedAt, run = null) => {
    const working = new Map();
    return Object.fromEntries(tasks.map((task) => {
        if (task.actor !== 'MANUAL' && task.result && run && !run.has(task.id)) return [task.id, keep(task, working)];
        const result = processOne(task, inventory, working);
        return [task.id, { ...result, blocksStaging: BLOCKS.has(result.status) || result.ruleBreaks.length > 0, processedAt }];
    }));
};

export const manualResult = (processedAt) => ({ ...outcome('MANUAL'), blocksStaging: false, processedAt });

export const ruleBreaksFor = (rules, fileId, key, value) => rules
    .filter((r) => r.fileId === fileId && r.key === key)
    .map((r) => ruleBreak(r, key, value))
    .filter(Boolean);
